/*
  MEMO CUSTOM — 2026-08-21 — chargement différé des vidéos décoratives.

  Problème corrigé : les vidéos du méga-menu, du tiroir panier et du fond de pied
  de page portaient `autoplay` + `preload="metadata"`. Un conteneur `display:none`
  n'empêche PAS le téléchargement : sur la home mobile ces vidéos invisibles
  pesaient à elles seules ~26 Mo, et ça sur CHAQUE page du site.

  Le Liquid les rend désormais SANS `autoplay` et en `preload="none"` : seule
  l'affiche (`poster`) s'affiche, sans un octet de vidéo. Ce script les démarre
  quand elles deviennent réellement visibles — c'est lui qui remplace l'`autoplay`
  retiré, donc sans lui les vidéos resteraient sur leur affiche.

  DEUX déclencheurs indépendants, à dessein :
   1. un IntersectionObserver, pour les révélations au défilement ;
   2. un balayage à l'interaction (survol, clic, focus, fin de transition), pour
      les volets qui s'ouvrent — un menu ou un tiroir s'ouvre toujours sur un
      geste, et l'IO n'est pas notifié quand l'onglet est en arrière-plan.
  Chacun sait conclure seul : c'est la ceinture et les bretelles.

  Trois pièges que ce fichier traite explicitement :
   - `display:none` ne donne aucune boîte : c'est justement ce qui fait qu'un menu
     fermé ne télécharge rien. `visibility:hidden` et `opacity:0`, eux, laissent
     une boîte — d'où le contrôle du style calculé (`visibility` s'hérite, un
     parent caché suffit donc à la faire passer à `hidden`).
   - le fond du pied de page est en `position:fixed; inset:0` : sa propre boîte
     couvre toujours l'écran, elle ne dit rien. `data-xm-lazy-trigger` désigne
     alors les éléments à mesurer à sa place (le groupe de sections du pied de
     page). Le sélecteur peut en désigner plusieurs — un seul vu suffit.
   - l'opacité de ce même fond est pilotée par le JS de `footer.liquid` (0 quand le
     pied de page n'est pas atteint) : pour une vidéo à déclencheur explicite on ne
     regarde donc pas l'opacité, l'appelant sait mieux.

  MEMO CUSTOM — 2026-09-02 — la lecture reprend, elle ne se joue plus une seule fois.
  `start()` posait `data-xm-lazy-started` et refusait ensuite tout nouveau départ,
  alors que `evaluate()` met en pause dès que la vidéo est cachée. Résultat : la
  vidéo du méga-menu tournait en boucle à la première ouverture, puis restait figée
  sur sa dernière image à chaque réouverture. Le drapeau ne sert plus qu'à savoir
  s'il y a quelque chose à mettre en pause ; c'est `video.paused` qui empêche
  désormais les appels redondants à `play()`. Aucun octet de plus n'est téléchargé :
  la vidéo est déjà en mémoire, on ne fait que relancer sa lecture. `evaluate()`
  repasse en plus une fois 400 ms après chaque mise en pause, pour qu'une pause
  décidée sur une image de transition ne fige pas la vidéo jusqu'au geste suivant.

  MEMO CUSTOM, 2026-10-14 : moins de lectures de mise en page (INP / TBT mobile).
  Chaque balayage mesurait TOUTES les vidéos suivies (getBoundingClientRect +
  getComputedStyle), à chaque mutation du DOM pendant le chargement, à chaque
  survol, défilement, fin de transition : Lighthouse imputait à ce fichier plus de
  3 s de recalcul de style et de mise en page sur la collection mobile. Désormais
  l'IntersectionObserver (calculé par le navigateur, sans mise en page forcée)
  tient à jour qui est près de l'écran, et `evaluate()` ne mesure que ces
  vidéos-là, ou celles qui jouent (pour pouvoir les mettre en pause). Une vidéo
  que l'IO dit loin ne peut de toute façon pas jouer : même résultat, sans lecture.
  Le MutationObserver ne relance plus de balayage que s'il a trouvé une vidéo neuve.
*/
(function () {
  'use strict';

  var SEL = 'video[data-xm-lazy], video.mega-menu-video';
  var MARGIN = 300; /* px de marge autour de l'écran : on part juste avant d'être vu */

  var watched = []; /* { video, triggers: [Element] | null } */
  var pending = false;
  /* Élément observé -> isIntersecting du dernier rapport de l'IO (marge MARGIN comprise).
     Absent = pas encore de rapport : l'IO en envoie toujours un juste après observe(). */
  var near = new WeakMap();

  function maybeNear(entry) {
    var els = entry.triggers || [entry.video];
    for (var i = 0; i < els.length; i++) {
      if (near.get(els[i]) === true) return true;
    }
    return false;
  }

  function start(video) {
    if (video.preload === 'none') video.preload = 'auto';
    video.dataset.xmLazyStarted = '1';
    /* Déjà en lecture : ne pas rappeler play() à chaque balayage. En revanche une
       vidéo mise en pause parce qu'elle avait été cachée DOIT pouvoir repartir —
       c'est tout l'objet du correctif du 02/09/2026. */
    if (!video.paused) return;
    var played = video.play();
    if (played && played.catch) played.catch(function () {});
  }

  /* Une boîte de taille nulle = `display:none` (menu fermé, ou mauvais palier
     d'écran pour un fond ordi/mobile) : rien à charger. */
  function hasBox(el) {
    var r = el.getBoundingClientRect();
    return r.width > 0 && r.height > 0;
  }

  function inViewport(el) {
    var r = el.getBoundingClientRect();
    return r.bottom > -MARGIN && r.top < window.innerHeight + MARGIN &&
           r.right > -MARGIN && r.left < window.innerWidth + MARGIN;
  }

  function shown(el) {
    var cs = window.getComputedStyle(el);
    return cs.visibility !== 'hidden' && cs.opacity !== '0';
  }

  function shouldPlay(entry) {
    if (!hasBox(entry.video)) return false;

    if (entry.triggers) {
      /* Déclencheur explicite : ce sont les éléments désignés qui disent « c'est le
         moment », la boîte de la vidéo étant en position:fixed donc muette. On ne
         regarde pas son opacité : elle est pilotée par le JS de la section. */
      for (var i = 0; i < entry.triggers.length; i++) {
        if (hasBox(entry.triggers[i]) && inViewport(entry.triggers[i])) return true;
      }
      return false;
    }

    return inViewport(entry.video) && shown(entry.video);
  }

  function evaluate(entry) {
    /* Loin de l'écran (ou sans boîte) selon l'IO, et pas en lecture : rien à décider,
       donc rien à mesurer. C'est ce test qui évite les mises en page forcées. */
    if (!maybeNear(entry) && entry.video.paused) return;
    var play = shouldPlay(entry);
    if (play) { start(entry.video); return; }
    if (!entry.video.dataset.xmLazyStarted || entry.video.paused) return;

    entry.video.pause();
    /* Une mise en pause peut être un faux positif : à l'ouverture du méga-menu il
       existe une image où le volet sortant est déjà caché et le volet entrant pas
       encore mesurable — un balayage qui tombe pile là met en pause une vidéo qui
       est en réalité sous les yeux du visiteur, et rien ne la relançait tant que la
       souris ne bougeait plus. On repasse donc une fois, un peu plus tard.
       Ne boucle pas : une vidéo déjà en pause ressort à la ligne précédente. */
    window.setTimeout(sweep, 400);
  }

  /* Un seul balayage par image, quel que soit le nombre d'événements reçus. */
  function sweep() {
    if (pending) return;
    /* Aucune vidéo près de l'écran ni en lecture : `evaluate()` ne ferait rien, donc pas
       même de rAF (chaque survol, clic ou fin de transition en réservait un). */
    var any = false;
    for (var k = 0; k < watched.length && !any; k++) {
      any = maybeNear(watched[k]) || !watched[k].video.paused;
    }
    if (!any) return;
    pending = true;

    var ran = false;
    var run = function () {
      if (ran) return;
      ran = true;
      pending = false;
      for (var i = 0; i < watched.length; i++) evaluate(watched[i]);
    };

    /* rAF est la bonne horloge pour lire des positions, mais elle ne tourne PAS dans un
       onglet en arrière-plan : sans le minuteur en filet, `pending` resterait fermé et
       plus aucun balayage n'aboutirait pour le reste de la vie de la page. Le premier
       des deux qui arrive gagne. */
    window.requestAnimationFrame(run);
    window.setTimeout(run, 250);
  }

  var io = new IntersectionObserver(function (entries) {
    for (var i = 0; i < entries.length; i++) near.set(entries[i].target, entries[i].isIntersecting);
    sweep();
  }, { rootMargin: MARGIN + 'px' });

  function observe(video) {
    if (video.dataset.xmLazyBound) return;
    video.dataset.xmLazyBound = '1';

    var selector = video.getAttribute('data-xm-lazy-trigger');
    var found = selector ? document.querySelectorAll(selector) : [];
    var entry = { video: video, triggers: found.length ? Array.prototype.slice.call(found) : null };

    watched.push(entry);
    (entry.triggers || [video]).forEach(function (el) { io.observe(el); });
    evaluate(entry);
  }

  function scan(root) {
    var scope = root && root.querySelectorAll ? root : document;
    Array.prototype.forEach.call(scope.querySelectorAll(SEL), observe);
    if (root && root.matches && root.matches(SEL)) observe(root);
  }

  scan();

  /* Sections rendues côté client : méga-menu, tiroir panier, Section Rendering API. */
  new MutationObserver(function (mutations) {
    var before = watched.length;
    for (var i = 0; i < mutations.length; i++) {
      var added = mutations[i].addedNodes;
      for (var j = 0; j < added.length; j++) {
        if (added[j].nodeType === 1) scan(added[j]);
      }
    }
    /* Une mutation sans vidéo neuve ne change rien que l'IO ne voie déjà. */
    if (watched.length !== before) sweep();
  }).observe(document.documentElement, { childList: true, subtree: true });

  document.addEventListener('shopify:section:load', function (event) {
    scan(event.target);
  });

  [
    'pointerover', 'pointerdown', 'click', 'focusin', 'keyup',
    'transitionend', 'animationend', 'scroll', 'resize', 'visibilitychange'
  ].forEach(function (type) {
    document.addEventListener(type, sweep, { passive: true, capture: true });
  });
})();
